/**
 * "Check the sitemap before you spend money" gate (SS001-2).
 *
 * Both crawl-start buttons used to be live before the sitemap was ever read, so
 * an operator could start an unscoped crawl of a 50k-URL site with one click.
 * A crawl is a paid, slow, hard-to-undo action; the estimate is what makes its
 * size and cost visible, so it is a prerequisite, not a suggestion.
 *
 * These return a human-readable reason (used as both the disabled tooltip and
 * an inline note) or `null` when the button is safe to press.
 */

export interface AddDomainStartGateInput {
  domainInput: string;
  startingCrawl: boolean;
  estimateLoading: boolean;
  /** A sitemap estimate has been fetched **for the current domain input**. */
  hasEstimate: boolean;
  /**
   * A sitemap check ran for the current domain input and failed. Counts as
   * checked: the operator has seen the problem and may crawl anyway (same rule
   * as the batch path) — an unreadable sitemap must not be a dead end.
   */
  checkFailed?: boolean;
  /** Leaf sitemaps the estimate found — 0 means there is nothing to pick. */
  leafSitemapCount: number;
  selectedSitemapCount: number;
  localeGroupCount: number;
  selectedLocaleCount: number;
}

export function addDomainStartCrawlBlockedReason(input: AddDomainStartGateInput): string | null {
  if (input.startingCrawl) return 'Starting the crawl…';
  if (!input.domainInput.trim()) return 'Enter a domain first.';
  if (input.estimateLoading) return 'Checking the sitemap…';
  if (!input.hasEstimate) {
    return input.checkFailed
      ? null // checked, unreadable — starting is the operator's call
      : 'Click Check sitemap first — scope the crawl before spending on it.';
  }
  if (input.leafSitemapCount > 0 && input.selectedSitemapCount === 0) {
    return 'Select at least one sitemap to crawl.';
  }
  if (input.localeGroupCount > 0 && input.selectedLocaleCount === 0) {
    return 'Select at least one locale to crawl.';
  }
  return null;
}

export interface PrepRowGateState {
  domain: string;
  /** Already crawled/complete rows are skipped by Start all crawls. */
  crawlDone: boolean;
  hasEstimate: boolean;
  hasEstimateError: boolean;
}

export interface StartAllPrepGateInput {
  rows: PrepRowGateState[];
  startingAll: boolean;
  checkingAll: boolean;
}

/** Rows that `Start all crawls` would actually start. */
export function prepRowsToStart(rows: PrepRowGateState[]): PrepRowGateState[] {
  return rows.filter((row) => !row.crawlDone);
}

/**
 * Same gate for the batch path. A row whose sitemap check *failed* still counts
 * as checked — the operator has seen the problem and may want to crawl anyway,
 * so a failed estimate must not become a dead end.
 */
export function startAllPrepCrawlsBlockedReason(input: StartAllPrepGateInput): string | null {
  if (input.startingAll) return 'Starting crawls…';
  if (input.checkingAll) return 'Checking sitemaps…';
  if (input.rows.length === 0) return 'Add domains to the prep list first.';

  const toStart = prepRowsToStart(input.rows);
  if (toStart.length === 0) return 'Every domain in the prep list is already crawled.';

  const unchecked = toStart.filter((row) => !row.hasEstimate && !row.hasEstimateError);
  if (unchecked.length > 0) {
    const names = unchecked.slice(0, 3).map((row) => row.domain).join(', ');
    const more = unchecked.length > 3 ? ` +${unchecked.length - 3} more` : '';
    return `Run Check all sitemaps first — not checked yet: ${names}${more}.`;
  }

  return null;
}
